/* ===================================================================
   Seed library — corpus collections, eval sets, versioned check & ws
   bundles, and a worked example history run. Verbatim port of the
   inline definitions from the original App.tsx.
=================================================================== */
import { WORKSTREAMS } from "./constants";
import { cloneJSON, nowStr, accumulateDocumentRegister } from "./utils";
import {
  CHECKS,
  CHECK_IDS,
  AGENT_CHECK_IDS,
  BASE_CHECK_MAP,
  synthResult,
  meterCheck,
} from "./checks";
import { CORPUS } from "./data/corpusLumen";
import { CORPUS2 } from "./data/corpusAcme";
import { HELIX_CORPUS, HELIX_BUNDLE, HELIX_EVAL } from "./data/helix";
import { computeEval } from "./eval";
import type {
  CompletedRun,
  CorpusSet,
  CostState,
  CheckMap,
  EvalSet,
  ProcessedEntry,
  Approval,
  ActionLog,
  MemosMap,
  CheckVersion,
  CheckBundle,
  WorkstreamBundle,
  Workspace,
} from "./types";

/* Agent roster ids come from the unified check registry. */
const AGENT_IDS = AGENT_CHECK_IDS;

/* ---- corpus list ---- */
export const SEED_CORPUSES: CorpusSet[] = [
  {
    id: "corp_lumen",
    name: "Lumen Robotics — Series B",
    note: "Project Atlas · $45M Series B financing",
    updated: nowStr(),
    emails: CORPUS,
    log: ["Created from Project Atlas thread export", "15 emails imported"],
  },
  {
    id: "corp_acme",
    name: "Acme Health — MSA renewal",
    note: "Standalone commercial-contract thread",
    updated: "Mar 10, 2026",
    emails: CORPUS2,
    log: ["Created", "3 emails imported"],
  },
  HELIX_CORPUS as unknown as CorpusSet,
];

/* ---- workstream bundles ---- */
export const SEED_WS_BUNDLES: WorkstreamBundle[] = [
  {
    id: "wsb_lumen",
    name: "Lumen Series B — working groups",
    note: "Financing matter + related Acme MSA",
    createdAt: nowStr(),
    workstreams: cloneJSON(WORKSTREAMS) as unknown as WorkstreamBundle["workstreams"],
    log: ["Created from matter intake", "2 workstreams"],
  },
  {
    id: "wsb_acme",
    name: "Acme MSA — working group",
    note: "Standalone commercial matter",
    createdAt: "Mar 1, 2026",
    workstreams: [cloneJSON(WORKSTREAMS[1])] as unknown as WorkstreamBundle["workstreams"],
    log: ["Created", "1 workstream"],
  },
  HELIX_BUNDLE as unknown as WorkstreamBundle,
];

/* ---- eval sets ---- */
export const _lumenExpected: Record<number, any> = {};
CORPUS.forEach((e) => {
  const x = e.expect || {};
  _lumenExpected[e.id] = {
    catalog: x.catalog || ["unassoc"],
    conflict: !!x.conflict,
    multi: !!x.multi,
    privilege: !!x.privilege,
    newpart: !!x.newpart,
    phase: !!x.phase,
    noise: !!x.noise,
    doc_expectations: (e.attachments || []).map((a) => ({
      filename: a.filename,
      expected_type: a.doc_type,
      expected_version: a.version,
    })),
  };
});

export const SEED_EVALSETS: EvalSet[] = [
  {
    id: "eval_lumen",
    name: "Lumen base eval",
    corpusId: "corp_lumen",
    when: nowStr(),
    expected: _lumenExpected,
  },
  HELIX_EVAL as unknown as EvalSet,
];

/* ---- a fully-worked example run so History is populated on load ---- */
export function buildExampleRun(): CompletedRun {
  const corpus = SEED_CORPUSES[0];
  const emails = corpus.emails;
  const CM: CheckMap = BASE_CHECK_MAP;
  const thresholds = Object.fromEntries(CHECK_IDS.map((id) => [id, CM[id].tau]));
  const proc: ProcessedEntry[] = emails.map((e) => {
    const R = e.r || synthResult(e);
    const checks = (R.checks || []).filter((c: any) => CM[c.id]);
    return { email: e, checks, R: R as any };
  });
  const appr: Approval[] = [];
  emails.forEach((e) => {
    ((e.r && e.r.approvals) || []).forEach((a: any, k: number) => {
      const type = a[1];
      const chosen = type === "conflict" && a[5] ? a[5].correct : undefined;
      appr.push({
        key: e.id + "-" + k,
        wsId: (e.r!.catalogs as any)[0][0],
        type,
        label: a[2],
        detail: a[3],
        material: a[4],
        options: type === "conflict" ? a[5] : null,
        source: type === "deal-memo" ? a[5] : null,
        status: "approved",
        chosen,
        emailId: e.id,
      });
    });
  });
  const actionsLog: ActionLog[] = [];
  emails.forEach((e) => {
    ((e.r && e.r.actions) || []).forEach((a: any) =>
      actionsLog.push({ agent: a[0], text: a[1], emailId: e.id }),
    );
  });
  const perCheck: CostState["perCheck"] = {};
  let total = 0;
  let tok = 0;
  const perEmail: CostState["perEmail"] = [];
  emails.forEach((e) => {
    const R = e.r || synthResult(e);
    let ec = 0;
    (R.checks || []).forEach((c: any) => {
      if (!CM[c.id]) return;
      const u = meterCheck(CM[c.id], e);
      const cur = perCheck[c.id] || {
        inTok: 0,
        outTok: 0,
        cost: 0,
        model: u.model,
        calls: 0,
      };
      perCheck[c.id] = {
        inTok: cur.inTok + u.inTok,
        outTok: cur.outTok + u.outTok,
        cost: cur.cost + u.cost,
        model: u.model,
        calls: (cur.calls || 0) + (u.calls || 0),
        latency: u.latency || cur.latency,
      };
      total += u.cost;
      tok += u.inTok + u.outTok;
      ec += u.cost;
    });
    perEmail.push({ id: e.id, subject: e.subject, cost: ec, tok: 0 });
  });
  const cost: CostState = { total, perCheck, perEmail };
  const memos: MemosMap = {
    ws_lumen: {
      phase: "Post-Closing",
      fields: {
        "Round size": {
          value: "$45M",
          source: "Ridgeline confirmation (#4)",
          history: [{ value: "$40M", source: "Ridgeline term sheet (#1)" }],
        },
        "Pre-money": {
          value: "$180M",
          source: "Ridgeline confirmation (#4)",
          history: [{ value: "$175M", source: "Ridgeline term sheet (#1)" }],
        },
        "Liquidation pref": {
          value: "1x non-participating",
          source: "Resolved by lawyer",
          history: [],
        },
        "Option pool": { value: "11%", source: "Ridgeline confirmation (#4)", history: [] },
      },
    },
    ws_acme: { phase: "Documentation", fields: {} },
  };
  const ev = computeEval(proc, appr, thresholds, new Set(), _lumenExpected);
  const matters = [
    { id: "ws_lumen", name: WORKSTREAMS[0].name, phase: "Setup" },
    { id: "ws_acme", name: WORKSTREAMS[1].name, phase: "Documentation" },
  ];
  const checkList = CHECK_IDS.map((id) => ({ id, ...CM[id] }));
  const wsNameMap: Record<string, string> = {
    unassoc: "Unassociated",
    ws_lumen: WORKSTREAMS[0].name,
    ws_acme: WORKSTREAMS[1].name,
  };
  const documentRegister = accumulateDocumentRegister(proc);
  return {
    id: "run-EX-2041",
    when: "Jun 4, 2026, 4:12 PM",
    config: {
      corpus: corpus.name,
      emails: emails.length,
      checkVersion: "v2025.06.04",
      matters: matters.map((m) => m.name),
      evalName: "Lumen base eval",
      togglesOff: [],
      agentsOff: 0,
    },
    eval: ev.noEval
      ? null
      : {
          catPct: ev.catPct,
          detPct: ev.detPct,
          catOK: ev.catOK,
          catN: ev.catN,
          detOK: ev.detOK,
          detN: ev.detN,
        },
    usage: { total, perCheck, tok },
    approvalsCount: appr.length,
    replay: {
      processed: proc,
      approvals: appr,
      memos,
      actionsLog,
      cost,
      thresholds,
      toggledOff: [],
      activeAgents: [...AGENT_IDS],
      CM,
      checkList,
      wsNameMap,
      matters,
      documentRegister,
      evalResult: ev,
      evalName: "Lumen base eval",
      checkVersion: "v2025.06.04",
      corpus: corpus.name,
    },
  };
}

export const SEED_HISTORY: CompletedRun[] = [buildExampleRun()];

export const SEED_WORKSPACES: Workspace[] = [
  {
    id: "wsx-lumen",
    name: "Lumen Series B — baseline",
    createdAt: "Jun 4, 2026",
    status: "completed",
    completedAt: "Jun 4, 2026, 4:12 PM",
    run: SEED_HISTORY[0],
  },
  {
    id: "wsx-acme",
    name: "Acme MSA — pricing variant",
    createdAt: "Jun 5, 2026",
    status: "active",
    config: {
      corpusId: "corp_acme",
      wsBundleId: "wsb_acme",
      matters: ["ws_acme"],
      checkBundleId: "cb_default",
      checkVerId: "cv1",
      evalId: null,
      speedIdx: 1,
    },
  },
];

/* Re-export CHECKS for convenience so callers can import everything from seeds. */
export { CHECKS };
